import {
    screenshotModeEnabled,
    trimValue,
    parseJsonAttribute,
    parseEasingFunctionParameter,
} from '../utilities/utilities.js';

const settings = {
    cssProperties: {
        // The position of the element on screen; 0 = the top edge
        // is at or entirely below the bottom of the viewport, 1 =
        // the bottom edge is at or entirely above the top of the
        // viewport, 0.5 = the element is centered in the viewport
        overallProgress: '--scroll-overall-progress',

        // The position of the element's top point on screen;
        // 0 = at or below the bottom of the viewport, 1 = at or
        // above the top of the viewport
        topProgress: '--scroll-top-progress',

        // The position of the element's center point on screen;
        // 0 = at or below the bottom of the viewport, 1 = at or
        // above the top of the viewport
        centerProgress: '--scroll-center-progress',
    },

    containerSelector: '*[data-scroll]',

    defaultParameters: {
        endCap: 1.0,
        easing: 'power2.out',
    },
};

// Tracks the scroll animations present on the page
const scrollAnimations = [];

/** General class for animations that occur during scrolling */
export default class ScrollAnimation {
    /**
     * @constructor
     * @param {HTMLElement} container The container that contains the
     * scroll animation
     * @param {object?} defaultParameters Optional default parameters,
     * which still may be overridden by the "data-scroll" HTML attribute
     */
    constructor(
        container,
        defaultParameters = {},
    ) {
        this.container = container;
        this.overallProgress = 0;
        this.topProgress = 0;
        this.centerProgress = 0;

        // Record this instance
        scrollAnimations.push(this);

        // Parse any animation parameters provided via JSON in an HTML attribute
        this.parameters = Object.assign(
            {...settings.defaultParameters},
            defaultParameters,
            parseJsonAttribute(
                this.container,
                'data-scroll',
            ),
        );

        this.easingFunction = parseEasingFunctionParameter(
            this.parameters.easing,
        );

        // If this is the first animation added to the page,
        // perform our general initialization
        if (scrollAnimations.length === 1) {
            initializeAnimations();
        }
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .querySelectorAll(settings.containerSelector);

        for (let i=0; i<elements.length; i++) {
            new ScrollAnimation(elements[i]);
        }
    }

    /**
     * Signals that we should update our position within the animation
     * @param {number} overallProgress
     * @param {number} topProgress
     * @param {number} centerProgress
     */
    setAnimationProgress(
        overallProgress,
        topProgress,
        centerProgress,
    ) {
        if (overallProgress != this.overallProgress) {
            // Store raw value
            this.overallProgress = overallProgress;

            // Apply our parameters
            let adjustedValue = applyParameters.call(
                this,
                overallProgress,
            );

            // Update CSS properties
            adjustedValue = trimValue(adjustedValue);

            this.container.style.setProperty(
                settings.cssProperties.overallProgress,
                `${adjustedValue}`,
            );
        }

        if (topProgress != this.topProgress) {
            // Store raw value
            this.topProgress = topProgress;

            // Apply our parameters
            let adjustedValue = applyParameters.call(
                this,
                topProgress,
            );

            // Update CSS properties
            adjustedValue = trimValue(adjustedValue);

            this.container.style.setProperty(
                settings.cssProperties.topProgress,
                `${adjustedValue}`,
            );
        }

        if (centerProgress != this.centerProgress) {
            // Store raw value
            this.centerProgress = centerProgress;

            // Apply our parameters
            let adjustedValue = applyParameters.call(
                this,
                centerProgress,
            );

            // Update CSS properties
            adjustedValue = trimValue(adjustedValue);

            this.container.style.setProperty(
                settings.cssProperties.centerProgress,
                `${adjustedValue}`,
            );
        }
    }
}

/*
    Internal functions
*/

function initializeAnimations() {
    // Check if screenshot mode is enabled
    if (screenshotModeEnabled) {
        // Set all scroll animations to their end state
        setTimeout(() => {
            scrollAnimations.forEach((scrollAnimation) => {
                // Update the animation
                scrollAnimation.setAnimationProgress(
                    1,
                    1,
                    1,
                );
            });
        });

        // Stop here
        return;
    }

    // Setup our tick function to monitor the current scroll position
    const tick = function() {
        scrollAnimations.forEach((scrollAnimation) => {
            // Check the location of our container with respect to the current
            // scroll position
            const bounds = scrollAnimation.container.getBoundingClientRect();
            let newOverallProgress;
            let newTopProgress;
            let newCenterProgress;

            if (bounds.top >= window.innerHeight) {
                // The container is fully out of view below the viewport
                newOverallProgress = 0;
                newTopProgress = 0;
                newCenterProgress = 0;
            } else if (bounds.bottom <= 0) {
                // Our container is fully out of view above the viewport
                newOverallProgress = 1;
                newTopProgress = 1;
                newCenterProgress = 1;
            } else {
                // The container is partially within, fully within, or spanning the viewport
                const elementHeight = bounds.bottom - bounds.top;
                const overallDistance = window.innerHeight + elementHeight;

                newOverallProgress = 1 - (
                    bounds.bottom / overallDistance
                );

                if (bounds.top >= window.innerHeight) {
                    newTopProgress = 0;
                } else if (bounds.top <= 0) {
                    newTopProgress = 1;
                } else {
                    newTopProgress = 1 - (
                        bounds.top / window.innerHeight
                    );
                }

                const centerPosition = bounds.top + (elementHeight / 2);
                if (centerPosition >= window.innerHeight) {
                    newCenterProgress = 0;
                } else if (centerPosition <= 0) {
                    newCenterProgress = 1;
                } else {
                    newCenterProgress = 1 - (
                        centerPosition / window.innerHeight
                    );
                }
            }

            // Update the animation
            scrollAnimation.setAnimationProgress(
                newOverallProgress,
                newTopProgress,
                newCenterProgress,
            );
        });

        // Request next animation frame
        window.requestAnimationFrame(tick);
    };

    // Start loop on the next thread execution - this gives components time
    // to finish initializing anything needed during the first tick call
    setTimeout(tick, 1);
}

/**
 * @this {ScrollAnimation}
 * @param {number} value
 * @return {number}
 */
function applyParameters(value) {
    // Apply cap
    if (this.parameters.endCap) {
        if (value >= this.parameters.endCap) {
            value = 1;
        } else {
            value = value / this.parameters.endCap;
        }
    }

    // Apply easing
    value = this.easingFunction(value);

    return value;
}
